import { useEffect, useRef, ImgHTMLAttributes } from "react";
import { PLACEHOLDER_URL, manifestToSupabase } from "@/lib/imageUtils";

interface ResilientImgProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Max retry attempts after initial + fallback try (default 2) */
  retries?: number;
  /** Delay between retries in ms (default 300) */
  delay?: number;
  /**
   * Last-resort URL tried before the placeholder. Use for the raw
   * /object/ original when every responsive candidate is a transform URL
   * (e.g. a source the Render API refuses as "resolution too large").
   */
  fallbackSrc?: string;
}

/**
 * Drop-in `<img>` replacement with smart retry logic:
 * 1. If Render API URL fails → falls back to raw /object/ URL (strips resize params)
 * 2. Subsequent failures → retries with cache-busting param
 * 3. Final failure → shows /placeholder.svg
 *
 * Uses pre-flight `new Image()` so the real DOM element never flickers.
 */
export default function ResilientImg({
  src,
  retries = 2,
  delay = 300,
  alt = "",
  srcSet,
  sizes,
  fallbackSrc,
  ...props
}: ResilientImgProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  // Tracks whether the element already shows a successfully decoded image.
  // Once it does, a later `src` change (e.g. the manifest "upgrade" swap)
  // must NOT be written to the DOM before the new URL has preloaded —
  // doing so blanks the frame and shifts the layout around the card.
  const hasPaintedRef = useRef(false);
  // Guard against empty string src — `<img src="">` makes browsers
  // request the current page URL, generating spurious 404 warnings.
  const safeSrc = src && String(src).trim() ? src : PLACEHOLDER_URL;

  useEffect(() => {
    if (!imgRef.current) return;
    const effective = safeSrc;

    let cancelled = false;

    const applyToImg = (url: string, withResponsive: boolean) => {
      if (!imgRef.current) return;
      if (withResponsive && srcSet) {
        if (sizes) imgRef.current.sizes = sizes;
        imgRef.current.srcset = srcSet;
      } else {
        imgRef.current.removeAttribute("srcset");
      }
      imgRef.current.src = url;
    };

    const tryLoad = (attempt: number, url: string) => {
      const test = new Image();
      // Pre-flight with the same responsive hints so the browser picks the
      // right candidate during the test load.
      if (srcSet) {
        if (sizes) test.sizes = sizes;
        test.srcset = srcSet;
      }

      test.onload = () => {
        if (cancelled) return;
        // First attempt: keep srcSet so browser does responsive selection.
        applyToImg(url, attempt === 0);
        hasPaintedRef.current = true;
      };

      test.onerror = () => {
        if (cancelled) return;

        // Attempt 0: if Render API failed, try raw /object/ URL
        if (attempt === 0 && url.includes("/render/image/")) {
          const fallback = url.replace("/render/image/", "/object/").split("?")[0] ?? url;
          return tryLoad(1, fallback);
        }

        // Attempt 0: prebuilt manifest variant missing (builder hasn't
        // reached the CDN mirror yet). Try the Supabase storage origin copy
        // before giving up on the variant entirely.
        if (attempt === 0) {
          const originCopy = manifestToSupabase(url);
          if (originCopy !== url) return tryLoad(0, originCopy);
        }

        // Prebuilt variant missing at both hosts (builder hasn't
        // processed this source yet). Retry once against the original `src`
        // prop — that's the raw object URL or CDN mirror the caller passed
        // in, which always exists when the manifest is behind.
        if (
          attempt < 1 &&
          /\/splash-assets\/manifest\/[0-9a-f]{64}\/(thumb|card|detail|hero)\.webp/.test(url) &&
          typeof src === "string" &&
          src &&
          src !== url
        ) {
          return tryLoad(1, src);
        }

        // Caller-supplied last resort (raw original) before the placeholder.
        if (fallbackSrc && url !== fallbackSrc && attempt < 3) {
          return tryLoad(3, fallbackSrc);
        }

        // After fallback failed (attempt 1) or out of retries → placeholder
        applyToImg(PLACEHOLDER_URL, false);
      };

      test.src = url;
    };

    // Set initial attributes immediately (avoids blank frame) ONLY on the very
    // first paint. Afterwards we wait for the preflight load to succeed so the
    // visible image is never cleared mid-swap.
    const alreadyPainted =
      hasPaintedRef.current || (imgRef.current.complete && imgRef.current.naturalWidth > 0);
    if (!alreadyPainted) {
      applyToImg(effective, true);
    } else {
      hasPaintedRef.current = true;
    }
    tryLoad(0, effective);

    return () => {
      cancelled = true;
    };
  }, [safeSrc, src, srcSet, sizes, retries, delay, fallbackSrc]);

  return <img ref={imgRef} alt={alt} src={safeSrc} {...props} />;
}
